import { Component, Input } from '@angular/core';
import { AbstractControl, UntypedFormGroup } from '@angular/forms';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import { AutoCompleteComponent, IAutoCompleteItem } from 'auto-complete-lib';
import { INamedEntity } from 'interfaces-lib';
import { Observable, of } from 'rxjs';

export type FilterState<T> = {
  formGroup: UntypedFormGroup | undefined;
  readonly srcList: Readonly<T>[];
};

@Component({
  selector: 'app-user-restrictions-filter',
  templateUrl: './user-restrictions-filter.component.html',
  styleUrls: ['./user-restrictions-filter.component.scss'],
  standalone: true,
  imports: [AutoCompleteComponent, MatIconModule, MatTooltipModule],
})
export class UserRestrictionsFilterComponent<T extends INamedEntity> {
  /**
   * Title of the filter.
   */
  @Input()
  title = '';

  /**
   * Tooltip of the filter.
   */
  @Input()
  tooltip = '';

  /**
   * The original data source items that will be filtered and the current selection.
   */
  @Input()
  set filterState(fs: FilterState<T> | undefined) {
    this._filterState = fs;

    if (this._filterState) {
      this.valuesControl.value.forEach((id: string | number) => {
        const found = this._filterState.srcList.find((item: T) => item.id === id);
        if (!found) {
          console.warn(`Item '${id}' not found in source data list.`, id);
          // TODO: should we remove the item from the values control?
        } else {
          this.selectedFilterItems.push(found);
        }
      });
      this.selectedFilterItems.sort((a, b) => a.name.localeCompare(b.name));
      this.disabled = false;
    } else {
      this.selectedFilterItems = [];
      this.disabled = true;
    }
  }

  disabled = true;
  selectedFilterItems: T[] = [];

  private _filterState: FilterState<T> | undefined;

  filterFn(filterValue: string): Observable<IAutoCompleteItem<T>[]> {
    filterValue = filterValue.toLowerCase().trim();
    const result = this._filterState.srcList
      .filter((item) => !this.selectedFilterItems.includes(item))
      .filter((item) => item.name.toLowerCase().includes(filterValue))
      .map((item) => ({ label: item.name, data: item }))
      .sort((a, b) => a.label.localeCompare(b.label));
    return of(result);
  }

  onSelectFilterItem(item: T): void {
    this.selectedFilterItems.push(item);
    this.selectedFilterItems.sort((a, b) => a.name.localeCompare(b.name));

    const valuesControl = this._filterState.formGroup.get('filterValues');
    valuesControl.value.push(item.id);

    this._filterState.formGroup.markAsDirty();
  }

  onRemoveFilterItem(id: string): void {
    this.selectedFilterItems = this.selectedFilterItems.filter((ut) => ut.id !== id);

    const valuesControl = this._filterState.formGroup.get('filterValues');
    let values = valuesControl.value as string[];
    values = values.filter((vid) => vid !== id);
    valuesControl.setValue(values);

    this._filterState.formGroup.markAsDirty();
  }

  private get valuesControl(): AbstractControl {
    return this._filterState.formGroup.get('filterValues');
  }
}
